import { hash } from '../../../CodesThatsGottaGoSomewhereElse';
import { defineCommand } from '../../../concepts/commands/CommandDefinition';
import { CommandDispatcher } from '../../../concepts/commands/CommandDispatcher';
import { inCommands } from '../../../concepts/commands/CommandRegistry';
import { numberStep, pointStep, stringStep } from '../../../concepts/commands/CommandWizard';
import { ObjectManager } from '../../../concepts/objects';
import { Line } from '../../../platform/geom/Line';
import { Matrix } from '../../../platform/geom/Matrix';
import { Point } from '../../../platform/geom/Point';
import { Polyline } from '../../../platform/geom/Polyline';
import { Rect } from '../../../platform/geom/Rect';
import { Activator } from '../../Container';
import { Physical, RelativeTo, TransformScope } from '../entities/Physical';
import { Physicals } from '../entities/Physicals';
import { Resizable } from '../entities/Resizable';
import { GridSystem } from '../GridSystem';
import { SelectionScaleTransformation } from '../tooling/transform/SelectionScaleTransformation';
import { resolveSubjects } from './CommandUtil';
import { place } from './OrderCommands';

export const align = defineCommand(
    'align',
    'Transform: Align...',
    (r, [targets]: [string[]?]) => {

        const grid = r.call(GridSystem);
        const objects = r.call(ObjectManager);

        const subjects = Physicals.top(resolveSubjects(r, targets));
        const offsets = new Map<Physical, Point>();
        const resizes = new Map<Resizable, Point>();

        for (const s of subjects) {
            {
                const b = s.position(RelativeTo.Model);
                const a = grid.snap(b);
                if (!b.equals(a)) {
                    offsets.set(s, a.subtract(b));
                }
            }
            if (s.is(Resizable)) {
                const b = s.size();
                const a = grid.snap(b);
                if (!b.equals(a)) {
                    resizes.set(s as Resizable, a);
                }
            }
        }

        if (!offsets.size && !resizes.size) return;

        objects.update(() => {
            for (const [p, o] of offsets) {
                p.moveBy(o);
            }
            for (const [p, s] of resizes) {
                p.resize(s.w, s.h);
            }
        });
    }
);

export type MoveDirection = 'up' | 'down' | 'left' | 'right';
export type MoveStep = 'unit' | 'grid' | 'bounds' | number;

const moveFactor = (ms: MoveStep, subjects: Physical[]): Point => {
    if (typeof (ms) === 'string') {
        if (ms === 'unit') {
            return new Point(1, 1);
        }
        else if (ms === 'bounds') {
            return Rect.fromMany(subjects.map(x => x.bounds(RelativeTo.Model))).size();
        }
        else if (ms.match(/grid\d+x/)) {
            const mult = parseInt(ms.slice(4)) || 1;
            return new Point(10 * mult, 10 * mult);
        }
        else {
            return new Point(10, 10);
        }
    }
    return new Point(ms, ms);
};

const moveInfo = {
    hash: '',
    count: 0,
    time: -10000,
};

export const move = defineCommand(
    'move',
    'Transform: Move...',
    (r, [direction, step, targets]: [MoveDirection, MoveStep, string[]?]) => {

        const dispatch = r.call(CommandDispatcher);
        const objects = r.call(ObjectManager);

        const subjects = Physicals.top(resolveSubjects(r, targets));

        const factor = moveFactor(step, subjects);
        const vector = Point[direction].multiply(factor);

        if (!!subjects.find(x => x.locked)) {
            return;
        }

        // This is pretty hacky but it's an easy way to prevent move spamming the history stack
        const inputHash = hash('sha256', subjects.map(x => x.id).join());
        const time = performance.now();

        if (inputHash !== moveInfo.hash || (time - moveInfo.time) > 2000) {
            moveInfo.count++;
        }

        moveInfo.hash = inputHash;
        moveInfo.time = time;

        objects.update(u => {
            u.set('key', `move:${moveInfo.count}`);
            const ts = new TransformScope(subjects, 'translate');
            subjects.forEach(s => {
                if (s.parentPhysical) {
                    const from = s.createFigure();
                    const to = Matrix.translate(vector).apply(from);
                    s.applyTransformation(ts, from, to);
                }
            });
            dispatch(place, [subjects]);
        });
    },
    [
        stringStep('direction', 'Direction:', 'right'),
        numberStep('by', 'Amount:', 10),
    ]
);

export const moveToEdge = defineCommand(
    'moveToEdge',
    'Transform: Move To Edge...',
    (r, [edge, targets]: ['top' | 'bottom' | 'left' | 'right', string[]?]) => {

        const dispatch = r.call(CommandDispatcher);
        const objects = r.call(ObjectManager);

        const subjects = Physicals.top(resolveSubjects(r, targets));
        const vector = Point[(edge === 'top') ? 'up' : (edge === 'bottom') ? 'down' : edge];

        // Ignore if any locked, not uniform parent and parent has no size
        if (!subjects.length) return;
        if (!!subjects.find(x => x.locked)) return;
        if (new Set(subjects.map(x => x.parent)).size !== 1) return;
        if (!!subjects[0].parentPhysical?.size().colinear) return;

        const parent = subjects[0].parentPhysical;
        const bounds = Rect.fromMany(subjects.map(x => x.bounds(RelativeTo.Parent)));

        let distance = bounds.edge(edge);
        if (edge === 'bottom') distance = parent.size().h - distance;
        if (edge === 'right') distance = parent.size().w - distance;

        const subjectHash = hash('sha256', subjects.map(x => x.id).join());

        objects.update(u => {
            u.set('key', `moveToEdge:${subjectHash}`);
            const ts = new TransformScope(subjects, 'translate');
            subjects.forEach(s => {
                const from = s.createFigure();
                const to = Matrix.translate(vector.multiply(distance)).apply(from);
                s.applyTransformation(ts, from, to);
            });
            // dispatch(place, [subjects]);
        });
    }
);

const fitValuesLookup = {
    'top': [.5, 1, .5, 0],
    'bottom': [.5, 0, .5, 1],
    'left': [1, .5, 0, .5],
    'right': [0, .5, 1, .5],
};

const fitScaleInputs = (axis: Point, subjects: Physical[]) => {

    // This code is kinda confusing.  It's basically taking the corners of the true (rotated) bounds of the selection (abcd), getting the
    // mid points (wxyz).  We need each of these points but the usage depends on the axis.  The desired output is an origin and handle for
    // the scale, then the final two points as the upper and lower bounds (used to compute the collision with the parent bounds later).

    const [a, b, c, d] = subjects.length === 1
        ? subjects[0].outline(RelativeTo.Model).points
        : Physicals.bounds(subjects).points();

    const [w, x, y, z] = Polyline.from([a, b, c, d], true).lines.map(x => x.mid);

    // Return format is [origin, handle, upper, lower]

    // East
    if (axis.x > 0) return [z, x, b, c];
    // West
    if (axis.x < 0) return [x, z, a, d];
    // North
    if (axis.y < 0) return [y, w, a, b];
    // South
    if (axis.y > 0) return [w, y, c, d];
    return [];
};

export const fitToEdge = defineCommand(
    'fitToEdge',
    'Transform: Fit To Edge...',
    (r, [axis, targets]: [Point, string[]?]) => {

        const activate = r.call(Activator);
        const subjects = Physicals.top(resolveSubjects(r, targets));

        // Ignore if any locked, not uniform parent and parent has no size
        if (!subjects.length) return;
        if (!!subjects.find(x => x.locked)) return;
        if (new Set(subjects.map(x => x.parent)).size !== 1) return;
        if (!!subjects[0].parentPhysical?.size().colinear) return;

        const [origin, handle, upper, lower] = fitScaleInputs(axis, subjects);
        if (!origin || !handle || !upper || !lower) return;

        // DebugOverlay.point('origin', origin, 'purple', 10);
        // DebugOverlay.point('handle', handle, 'pink', 10);
        // DebugOverlay.point('upper', upper, 'blue', 10);
        // DebugOverlay.point('lower', lower, 'blue', 10);

        // Direction unit vector
        const vector = Point.vector(origin, handle).normalize();
        // Parent borders to collision detect
        const borders = subjects[0].parentPhysical.outline(RelativeTo.Model).lines;
        // Upper collision
        const ur = Line.ray(upper, vector);
        const uc = borders.map(x => x.intersectPoint(ur)).find(x => !!x);
        const ud = uc?.distanceTo(upper) || 0;
        // Lower collision
        const lr = Line.ray(lower, vector);
        const lc = borders.map(x => x.intersectPoint(lr)).find(x => !!x);
        const ld = lc?.distanceTo(lower) || 0;

        // Select shortest distance
        const dist = Math.min(ud, ld);
        if (!dist) return;

        // DebugOverlay.point('to', handle.add(vector.multiply(dist)), 'red', 10);
        // DebugOverlay.point('lc', lc, 'red', 10);
        // DebugOverlay.point('uc', uc, 'red', 10);

        // Perform the scale
        const scale = activate.call(SelectionScaleTransformation);
        scale.configure({
            origin,
            axis,
            handle,
            fixedMode: false,
            forceSnapping: false
        });
        scale.begin({ position: handle });
        scale.continue({ position: handle.add(vector.multiply(dist)) });
        scale.complete();
    },
    [
        pointStep('Enter axis:')
    ]
);

const dimExprResolver2 = (input: string) => {
    if (!input) return null;
    if (input.includes('/')) {
        const [a, b] = input.split('/').map(parseFloat);
        input = `${(a / b) * 100}%`;
    }
    if (input.endsWith('%')) {
        return (dim: number) => {
            const v = parseFloat(input) / 100;
            return dim * v;
        };
    }
    return (_dim: number) => {
        return parseFloat(input) || null;
    };
};

export const setWidths = defineCommand(
    'setWidths',
    'Transform: Set Widths...',
    (r, [input, targets]: [string, (string | string[])?]) => {

        const dispatch = r.call(CommandDispatcher);
        const objects = r.call(ObjectManager);
        const subjects = resolveSubjects(r, targets);
        if (!subjects.length) return;

        const calc = dimExprResolver2(input);
        if (!calc) return;

        objects.update(() => {
            for (const s of subjects) {

                const f = s.createFigure();
                const fb = Rect.fromPoints(f);
                const newWidth = calc(fb.width) ?? fb.width;

                const scale = newWidth / fb.width;

                const mt = Matrix
                    .translate(fb.left, fb.top)
                    .scale(scale, 1)
                    .translate(-fb.left, -fb.top);

                s.setFromFigure(mt.apply(f));
            }

            dispatch(place, [subjects]);
        });
    },
    [
        stringStep('input', 'Enter a height:'),
    ]
);

export const setHeights = defineCommand(
    'setHeights',
    'Transform: Set Heights...',
    (r, [input, targets]: [string, (string | string[])?]) => {

        const dispatch = r.call(CommandDispatcher);
        const objects = r.call(ObjectManager);
        const subjects = resolveSubjects(r, targets);
        if (!subjects.length) return;

        const calc = dimExprResolver2(input);
        if (!calc) return;

        objects.update(() => {
            for (const s of subjects) {

                const f = s.createFigure();
                const fb = Rect.fromPoints(f);
                const newHeight = calc(fb.height) ?? fb.height;

                const scale = newHeight / fb.height;

                const mt = Matrix
                    .translate(fb.left, fb.top)
                    .scale(1, scale)
                    .translate(-fb.left, -fb.top);

                s.setFromFigure(mt.apply(f));
            }

            dispatch(place, [subjects]);
        });
    },
    [
        stringStep('input', 'Enter a height:'),
    ]
);

export const setSizes = defineCommand(
    'setSizes',
    'Transform: Set Sizes...',
    (r, [width, height, targets]: [string, string, (string | string[])?]) => {

        const dispatch = r.call(CommandDispatcher);
        const objects = r.call(ObjectManager);

        objects.update(() => {
            dispatch(setWidths, [width, targets]);
            dispatch(setHeights, [height, targets]);
        });
    },
    [
        stringStep('input', 'Enter a height:'),
    ]
);

export const inTransformCommands = () => inCommands(
    align,
    move,
    moveToEdge,
    fitToEdge,
    setWidths,
    setHeights,
    setSizes,
);
